import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { PendingTimeBlock, PendingTimeService } from '../../services/pending-time.service';

interface RecoveryItem {
  block: PendingTimeBlock;
  minutes: number;
  description: string;
  busy: boolean;
  error: string;
  confirmDiscard: boolean;
}

/** Shown after login when unlogged time from an earlier session was saved as a draft. It can not be dismissed. */
@Component({
  selector: 'app-pending-time-recovery-modal',
  templateUrl: './pending-time-recovery-modal.component.html',
  styleUrls: ['./pending-time-recovery-modal.component.scss']
})
export class PendingTimeRecoveryModalComponent implements OnInit, OnDestroy {
  @ViewChild('recoveryModal', {static: false}) recoveryModal: ModalDirective;

  items: RecoveryItem[] = [];
  busyAll = false;
  private subscription: Subscription;
  private shown = false;

  constructor(private pendingTimeService: PendingTimeService) {
  }

  ngOnInit(): void {
    this.subscription = this.pendingTimeService.blocks$.subscribe(blocks => {
      const previous = this.items;
      this.items = blocks.map(block => previous.find(i => this.key(i.block) === this.key(block)) || {
        block: block, minutes: block.minutes, description: 'Chart Review and Monitoring',
        busy: false, error: '', confirmDiscard: false
      });
      this.items.forEach(i => i.block = blocks.find(b => this.key(b) === this.key(i.block)));
      setTimeout(() => this.items.length ? this.show() : this.hide());
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  key(block: PendingTimeBlock): string {
    return block.ids.join('-');
  }

  trackItem(index: number, item: RecoveryItem): string {
    return item.block.ids.join('-'); // trackBy runs without `this`
  }

  get anyLoggable(): boolean {
    return this.items.some(i => this.canLog(i));
  }

  canLog(item: RecoveryItem): boolean {
    return !item.block.locked && !item.busy && !this.busyAll && Number(item.minutes) > 0;
  }

  log(item: RecoveryItem): Promise<void> {
    item.busy = true;
    item.error = '';
    return this.pendingTimeService.logBlock(item.block, Number(item.minutes), item.description)
      .catch(err => this.handleError(item, err))
      .then(() => {
        item.busy = false;
      });
  }

  logAll(): void {
    const loggable = this.items.filter(i => this.canLog(i)); // before busyAll, canLog() is false while it is set
    this.busyAll = true;
    loggable.reduce((chain, item) => chain.then(() => this.log(item)), Promise.resolve())
      .then(() => {
        this.busyAll = false;
      });
  }

  discard(item: RecoveryItem): void {
    item.busy = true;
    item.error = '';
    this.pendingTimeService.discardBlock(item.block, item.block.locked ? 'billed_month' : 'user_discarded')
      .catch(err => this.handleError(item, err))
      .then(() => {
        item.busy = false;
        item.confirmDiscard = false;
      });
  }

  private handleError(item: RecoveryItem, err: any): void {
    const body = err && err.error ? err.error : {};
    if (body.code === 'month_billed') {
      item.block.locked = true;
    }
    if (body.code === 'already_resolved' || body.code === 'not_found') {
      this.pendingTimeService.loadRecovery();
    }
    item.error = body.error || 'Could not save. Please try again.';
  }

  private show(): void {
    if (this.recoveryModal && !this.shown) {
      this.shown = true;
      this.recoveryModal.show();
    }
  }

  private hide(): void {
    if (this.recoveryModal && this.shown) {
      this.shown = false;
      this.recoveryModal.hide();
    }
  }
}
